import {
	apiServices,
	createUserVisibleError,
	getApiErrorMessage,
} from "../../../../core/utilities/ApiServices";
import LocationDetails from "../../domain/entities/LocationDetails";
import Search from "../../domain/entities/Search";
import {
	VerificationLivenessSession,
	VerificationUploadResult,
} from "../../domain/entities/VerificationUploadResult";

export interface SettingsDataSource {
	getVerificationCode(): Promise<string>;
	getPlaces(search: string):Promise<Search[]>;
	getDetailsPlace(place_id: string):Promise<LocationDetails>;
	sendVerificationPhoto(
		file: File,
		code: string
	): Promise<VerificationUploadResult>;
	createVerificationLivenessSession(): Promise<VerificationLivenessSession>;
	saveVerificationLivenessResult(sessionId: string): Promise<string>;
	createAdminLiveFaceSession(): Promise<VerificationLivenessSession>;
	saveAdminLiveFaceResult(sessionId: string): Promise<string>;
}

export class SettingsDataSourceImpl implements SettingsDataSource {
	
	async getPlaces(search: string): Promise<Search[]> {
		// const timestamp = new Date().getTime();
		const response = await apiServices.get(`/location/search`,{params:{search}});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error("Error fetching places");
		}
		
	}

	async getDetailsPlace(place_id: string): Promise<LocationDetails> {
		const response = await apiServices.get(`/location/details`,{params:{place_id}});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error("Error fetching place details");
		}
	}

	async getVerificationCode(): Promise<string> {
		const response = await apiServices.get(`/profile/verification-code`);
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}

	async sendVerificationPhoto(
		file: File,
		code: string
	): Promise<VerificationUploadResult> {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("code", code);

		const response = await apiServices.post(
			`/profile/verification-photo`,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			}
		);

		if (response.status === 200 || response.status === 202) {
			return this.normalizeVerificationUploadResult(response.data);
		} else {
			throw new Error("Error en la carga de la foto de verificación");
		}
	}

	async createVerificationLivenessSession(): Promise<VerificationLivenessSession> {
		let response;
		try {
			response = await apiServices.post(`/profile/verification-liveness/session`);
		} catch (error) {
			throw createUserVisibleError(
				getApiErrorMessage(error, "Could not start the live check.")
			);
		}

		if (response.status === 200) {
			const session = this.normalizeVerificationLivenessSession(response.data?.session);
			if (session) {
				return session;
			}
		}

		throw new Error("Could not start the live check.");
	}

	async saveVerificationLivenessResult(sessionId: string): Promise<string> {
		let response;
		try {
			response = await apiServices.post(`/profile/verification-liveness/result`, {
				session_id: sessionId,
			});
		} catch (error) {
			throw createUserVisibleError(
				getApiErrorMessage(error, "Could not save the live check result.")
			);
		}

		if (response.status === 200) {
			const status = response.data?.session?.status;
			if (typeof status === "string" && status.trim()) {
				return status.trim().toLowerCase();
			}
		}

		throw new Error("Could not save the live check result.");
	}

	async createAdminLiveFaceSession(): Promise<VerificationLivenessSession> {
		let response;
		try {
			response = await apiServices.post(`/profile/admin-live-face/session`);
		} catch (error) {
			throw createUserVisibleError(
				getApiErrorMessage(error, "Could not start the live face check.")
			);
		}

		if (response.status === 200) {
			const session = this.normalizeVerificationLivenessSession(response.data?.session);
			if (session) {
				return session;
			}
		}

		throw new Error("Could not start the live face check.");
	}

	async saveAdminLiveFaceResult(sessionId: string): Promise<string> {
		let response;
		try {
			response = await apiServices.post(`/profile/admin-live-face/result`, {
				session_id: sessionId,
			});
		} catch (error) {
			throw createUserVisibleError(
				getApiErrorMessage(error, "Could not save the live face check result.")
			);
		}

		if (response.status === 200) {
			const status = response.data?.status;
			if (typeof status === "string" && status.trim()) {
				return status.trim().toLowerCase();
			}
		}

		throw new Error("Could not save the live face check result.");
	}

	private normalizeVerificationUploadResult(data: any): VerificationUploadResult {
		if (Array.isArray(data)) {
			const status = data[0]?.status;
			return {
				verificationPhotoStatus:
					typeof status === "string" && status.trim() ? status.trim() : undefined,
				requiresLiveness: false,
			};
		}

		if (!data || typeof data !== "object") {
			return { requiresLiveness: false };
		}

		const verificationPhotoStatus =
			typeof data.verification_photo?.status === "string" &&
			data.verification_photo.status.trim()
				? data.verification_photo.status.trim()
				: undefined;
		const requiresLiveness = data.requires_liveness === true;
		const session = data.liveness_session;
		const message =
			typeof data.message === "string" && data.message.trim()
				? data.message.trim()
				: typeof session?.trigger_reason === "string" && session.trigger_reason.trim()
				? session.trigger_reason.trim()
				: undefined;

		return {
			verificationPhotoStatus,
			requiresLiveness,
			message,
			livenessSession: requiresLiveness
				? this.normalizeVerificationLivenessSession(session)
				: undefined,
		};
	}

	private normalizeVerificationLivenessSession(
		session: any
	): VerificationUploadResult["livenessSession"] | undefined {
		if (!session || typeof session !== "object") {
			return undefined;
		}

		const credentials = session.client_credentials;
		const region =
			typeof credentials?.region === "string" && credentials.region.trim()
				? credentials.region.trim()
				: typeof session?.session_settings?.region === "string" &&
				  session.session_settings.region.trim()
				? session.session_settings.region.trim()
				: undefined;
		const sessionId =
			typeof session?.session_id === "string" && session.session_id.trim()
				? session.session_id.trim()
				: undefined;
		const accessKeyId =
			typeof credentials?.access_key_id === "string" &&
			credentials.access_key_id.trim()
				? credentials.access_key_id.trim()
				: undefined;
		const secretAccessKey =
			typeof credentials?.secret_access_key === "string" &&
			credentials.secret_access_key.trim()
				? credentials.secret_access_key.trim()
				: undefined;
		const sessionToken =
			typeof credentials?.session_token === "string" &&
			credentials.session_token.trim()
				? credentials.session_token.trim()
				: undefined;

		if (
			!sessionId ||
			!region ||
			!accessKeyId ||
			!secretAccessKey ||
			!sessionToken
		) {
			return undefined;
		}

		return {
			sessionId,
			region,
			accessKeyId,
			secretAccessKey,
			sessionToken,
			expiresAt:
				typeof credentials?.expires_at === "string"
					? credentials.expires_at
					: null,
		};
	}
}
